<template>
  <div class="ex-checkpoint-card">
    <template v-if="ideaMDP452">
      <ChatterDrawer
        v-if="criteriaChatterIconOnly && !suppressMdpChatterDrawer"
        :drawer="mdpChatterDrawer"
        :avatar="mdpChatterAvatar"
        :isEmployeeChatter="true"
        :type="mdpChatterTypeName"
        :typeValue="mdpChatterTypeValue"
        :listData="mdpChatterListData"
        object-type="checkpoint_evaluation"
        :object-id="resolvedInspectionId"
        :chatterList="mdpChatterList"
        :totalCheckpointChatter="mdpChatterTotal"
        @closeDrawer="onMdpChatterDrawerClose"
        @requestMoreCheckpointsChatter="loadMoreChatter"
      />
      <div class="ex-criteria-row">
        <CheckpointCriteriaBar :criteria_with_prefix="effectiveCriteriaWithPrefix" :level="level" />
        <div
          v-if="criteriaChatterIconOnly"
          class="ex-mdp-chatter-icon-btn"
          :class="{ 'ex-mdp-chatter-icon-btn--inactive': suppressMdpChatterDrawer }"
          :role="suppressMdpChatterDrawer ? 'presentation' : 'button'"
          :tabindex="suppressMdpChatterDrawer ? -1 : 0"
          @click="onMdpChatterIconActivate"
          @keydown.enter.prevent="onMdpChatterIconActivate"
          @keydown.space.prevent="onMdpChatterIconActivate"
        >
          <v-img class="ex-mdp-chatter-icon-btn__img" :src="mdpChatterIconSrc" alt="" />
        </div>
      </div>
      <div class="ex-body">
        <KnowledgeSourceCard
          class="ex-source"
          :type="knowledgeCardType"
          :thumbnail-url="knowledgeThumbnailUrl"
          :description="knowledgeDescriptionText"
          :hide-thumbnail="knowledgeHideThumbnail"
        />
        <div class="ex-badges">
          <button
            v-if="mdpCtaKind === 'complete_microlearning'"
            type="button"
            class="ex-mdp-cta-btn"
            @click="onMdpCtaClick"
          >
            <span class="ex-mdp-cta-btn__leading" aria-hidden="true">
              <svg
                width="22"
                height="20"
                viewBox="0 0 22 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M16 11.941V8.93541C16 8.75593 16 8.66619 15.9727 8.58696C15.9485 8.5169 15.9091 8.45309 15.8572 8.40014C15.7986 8.34027 15.7183 8.30013 15.5578 8.21987L11 5.94098M3 6.94098V13.7476C3 14.1195 3 14.3055 3.05802 14.4683C3.10931 14.6122 3.1929 14.7425 3.30238 14.8491C3.42622 14.9697 3.59527 15.0471 3.93335 15.2021L10.3334 18.1354C10.5786 18.2478 10.7012 18.304 10.8289 18.3262C10.9421 18.3459 11.0579 18.3459 11.1711 18.3262C11.2988 18.304 11.4214 18.2478 11.6666 18.1354L18.0666 15.2021C18.4047 15.0471 18.5738 14.9697 18.6976 14.8491C18.8071 14.7425 18.8907 14.6122 18.942 14.4683C19 14.3055 19 14.1195 19 13.7476V6.94098M1 5.94098L10.6422 1.11987C10.7734 1.05428 10.839 1.02148 10.9078 1.00857C10.9687 0.997142 11.0313 0.997142 11.0922 1.00857C11.161 1.02148 11.2266 1.05428 11.3578 1.11987L21 5.94098L11.3578 10.7621C11.2266 10.8277 11.161 10.8605 11.0922 10.8734C11.0313 10.8848 10.9687 10.8848 10.9078 10.8734C10.839 10.8605 10.7734 10.8277 10.6422 10.7621L1 5.94098Z"
                  stroke="white"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </span>
            <span class="ex-mdp-cta-btn__labels">
              <span class="ex-mdp-cta-btn__line">{{ mdpCtaLine1 }}</span>
              <span class="ex-mdp-cta-btn__line">{{ mdpCtaLine2 }}</span>
            </span>
            <span class="ex-mdp-cta-btn__chevron" aria-hidden="true">
              <svg
                width="10"
                height="18"
                viewBox="0 0 10 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1.5 1.5L8 9l-6.5 7.5"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </span>
          </button>
          <StandardButton
            v-else-if="mdpCtaUsesStandardButton"
            type="magenta"
            variant="primary"
            size="small"
            :label="mdpStandardCtaLabel"
            class="ex-mdp-standard-cta"
            @click="onMdpCtaClick"
          />
          <AsEmployeeInspectionStatusBadge
            v-if="inspectionBadgeVisible"
            :status="inspectionBadgeStatus"
            :avatar-url="inspectionAvatarUrl"
            :reason="inspectionBadgeReason"
          />
          <QuizCompletionStatusBadge v-if="quizBadgeVisible" :status="quizBadgeStatus" />
        </div>
      </div>
    </template>

    <template v-else>
      <AsEmployeeCheckpointCardActionsBar
        :level="level"
        :userInfo="userInfo"
        :instructions="instructions"
        :video="video"
        :inspection_status="inspection_status"
        :label="label"
        :checkpointInfo="checkpointInfo"
        :showChatter="showChatter"
        :reason="reason"
        :prefix="prefix"
        :suppressLeftCluster="false"
        :chatter-icon-src="chatterIconSrc"
        :chatter-list="chatterList"
        :total-checkpoint-chatter="totalChatter"
        @learn-more-click="$emit('learn-more-click', $event)"
        @chatter-open="openChatterDrawer"
        @requestMoreCheckpointsChatter="loadMoreChatter"
      />
      <ExCriteriaInstructionsCell
        :prefix="prefix"
        :criteria="criteria"
        :instructions="instructions"
        :videoThumbnail="videoThumbnail"
        :videoUrl="video"
        :rubric="checkpointInfo?.microlearning_instructions?.summary || {}"
        :class="{
          'card-checkpoint-area': video === null,
        }"
        class="ex-checkpoint-area"
      />
    </template>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import ExCriteriaInstructionsCell from "@components/InstructionsCell/ExCriteriaInstructionsCell.vue";
import QuizCompletionStatusBadge from "@components/QuizCompletionStatusBadge/QuizCompletionStatusBadge.vue";
import KnowledgeSourceCard from "@components/KnowledgeSourceCard/KnowledgeSourceCard.vue";
import AsEmployeeInspectionStatusBadge from "@components/AsEmployeeInspectionStatusBadge/AsEmployeeInspectionStatusBadge.vue";
import CheckpointCriteriaBar from "@components/CheckpointCriteriaBar/CheckpointCriteriaBar.vue";
import AsEmployeeCheckpointCardActionsBar from "@components/AsEmployeeCheckpointCardActionsBar/AsEmployeeCheckpointCardActionsBar.vue";
import ChatterDrawer from "@components/Drawer/ChatterDrawer.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import {
  fetchCheckpointEvaluationChatter,
  resolveAccountIdFromStorage,
  resolveCheckpointEvaluationRecordId,
} from "@components/utils/checkpointEvaluationChatter";

const emit = defineEmits([
  "learn-more-click",
  "complete-microlearning-click",
  "mdp-cta",
  "requestMoreCheckpointsChatter",
]);

const props = defineProps({
  /** Feature flag idea-MDP-452: compact header + source row + stacked badges. */
  ideaMDP452: {
    type: Boolean,
    default: false,
  },
  level: {
    type: String,
    default: "Apprentice",
    validator: function (value) {
      return ["Apprentice", "Professional", "Coach", "Master"].indexOf(value) !== -1;
    },
  },
  userInfo: {
    type: Object,
    default: () => ({}),
  },
  criteria: {
    type: String,
    default: null,
  },
  /** MDP-452 / CheckpointCriteriaBar copy (include prefix in the string). */
  criteria_with_prefix: {
    type: String,
    default: "",
  },
  instructions: {
    type: String,
    default: null,
  },
  /** Body copy for KnowledgeSourceCard when `ideaMDP452` is true. */
  description: {
    type: String,
    default: null,
  },
  video: {
    type: String,
    default: null,
  },
  inspection_status: {
    type: String,
    default: "Pending",
  },
  reason: {
    type: String,
    default: "",
  },
  label: {
    type: String,
    default: "Learn More",
  },
  checkpointInfo: {
    type: Object,
    default: () => ({}),
  },
  showChatter: {
    type: Boolean,
    default: false,
  },
  /** Optional chatter icon URL; MDP-452 defaults to `icons/chatter.svg` when unset. */
  chatterIconSrc: {
    type: String,
    default: null,
  },
  prefix: {
    type: String,
    default: "",
  },
  videoThumbnail: {
    type: String,
    default: null,
  },
  /** Microlearning family for MDP-452 KnowledgeSourceCard mapping. */
  type: {
    type: String,
    default: "NONE",
    validator: (v) => ["MQI", "MI", "MQ", "NONE"].includes(v),
  },
  video_thumbnail: {
    type: String,
    default: null,
  },
  quiz: {
    type: String,
    default: "Passed",
    validator: (v) => ["Pending", "Passed", "Passed and Done", "Failed", "None"].includes(v),
  },
  inspection: {
    type: String,
    default: "Passed",
    validator: (v) => ["Pending", "Passed", "Failed", "Not Applicable", "None"].includes(v),
  },
  /**
   * When true, MDP criteria chatter icon is non-interactive (hover styles only; drawer does not mount / open).
   * Use in Storybook or static previews; app checklist leaves this false.
   */
  suppressMdpChatterDrawer: {
    type: Boolean,
    default: false,
  },
  /** Account id for checkpoint_evaluation chatter; falls back to `currentAccountID` in localStorage. */
  accountId: {
    type: [String, Number],
    default: null,
  },
  /** Checkpoint evaluation / inspection id for chatter `record_id`. */
  inspectionId: {
    type: [String, Number],
    default: null,
  },
});

/** When `idea-MDP-452` is on, chatter opens only from the criteria-row icon (drawer inlined; no action bar). */
const criteriaChatterIconOnly = computed(() => props.ideaMDP452 && props.showChatter);

const mdpChatterIconAsset = require("@components/assets/icons/chatter.svg");

/** Custom / MDP default asset for the criteria-row chatter icon. */
const mdpChatterIconSrc = computed(() => props.chatterIconSrc || mdpChatterIconAsset);

const effectiveCriteriaWithPrefix = computed(() => {
  if (props.criteria_with_prefix) return props.criteria_with_prefix;
  if (props.criteria) return props.criteria;
  return "";
});

const knowledgeCardType = computed(() => {
  switch (props.type) {
    case "MQI":
      return "Video with TOC";
    case "MI":
      return "Video Step by Step";
    /** MQ uses the same non-stream thumb treatment as NONE (article placeholder, no video strip). */
    case "MQ":
      return "Article";
    case "NONE":
    default:
      return "Article";
  }
});

const knowledgeThumbnailUrl = computed(() => {
  if (props.type === "NONE" || props.type === "MQ") return "";
  return props.video_thumbnail || props.checkpointInfo?.thumbnail_url || "";
});

/** NONE: no media column (matches design: description + badges only). */
const knowledgeHideThumbnail = computed(() => props.type === "NONE");

const knowledgeDescriptionText = computed(() => {
  if (props.description) return props.description;
  if (props.instructions) return props.instructions;
  return props.checkpointInfo?.description || "";
});

/**
 * MDP-452 primary actions (design): complete microlearning, proceed to inspection, retake quiz.
 * `null` when the right column is badges only.
 */
const mdpCtaKind = computed(() => {
  if (!props.ideaMDP452) return null;
  if (props.type === "NONE") return null;
  if (props.type === "MQI") {
    if (props.quiz === "Pending" && props.inspection === "Pending") return "complete_microlearning";
    if (props.quiz === "Passed" && props.inspection === "Pending") return "proceed_inspection";
    /** Quiz cleared; inspection failed — ready to prepare / go again for inspection. */
    if (props.quiz === "Passed" && props.inspection === "Failed") return "proceed_inspection";
    if (props.quiz === "Failed" && props.inspection === "Pending") return "retake_quiz";
    return null;
  }
  if (props.type === "MI" && props.quiz === "None" && props.inspection === "Pending") {
    return "proceed_inspection";
  }
  if (props.type === "MQ") {
    if (props.quiz === "Pending" && props.inspection === "None") return "complete_microlearning";
    if (props.quiz === "Failed" && props.inspection === "None") return "retake_quiz";
    return null;
  }
  return null;
});

const mdpCtaUsesStandardButton = computed(
  () => mdpCtaKind.value === "proceed_inspection" || mdpCtaKind.value === "retake_quiz"
);

const mdpStandardCtaLabel = computed(() => {
  if (mdpCtaKind.value === "proceed_inspection") return "Prepare for Inspection";
  if (mdpCtaKind.value === "retake_quiz") return "Retake the Quiz";
  return "";
});

const mdpCtaLine1 = computed(() => "Complete");
const mdpCtaLine2 = computed(() => "Microlearning");

const inspectionBadgeVisible = computed(() => {
  if (!props.ideaMDP452 || props.inspection === "None") return false;
  if (mdpCtaKind.value === "complete_microlearning") return false;
  if (mdpCtaKind.value === "retake_quiz") return false;
  /** “Proceed” replaces the pending chip only; keep failed (and other) inspection badges visible. */
  if (mdpCtaKind.value === "proceed_inspection" && props.inspection === "Pending") return false;
  return true;
});

const inspectionBadgeStatus = computed(() => {
  switch (props.inspection) {
    case "Pending":
      return "waiting_for_coach";
    case "Passed":
      return "passed";
    case "Failed":
      return "failed";
    case "Not Applicable":
      return "na";
    default:
      return "waiting_for_coach";
  }
});

const inspectionAvatarUrl = computed(() => props.userInfo?.inspected_by_avatar || "");

const inspectionBadgeReason = computed(() => {
  const fromProp = String(props.reason ?? "").trim();
  if (fromProp) return fromProp;
  return String(props.checkpointInfo?.reason ?? "").trim();
});

const quizBadgeVisible = computed(() => {
  if (!props.ideaMDP452 || props.quiz === "None") return false;
  if (mdpCtaKind.value === "complete_microlearning") return false;
  return true;
});

const quizBadgeStatus = computed(() => {
  if (props.quiz === "Pending") return "Pending";
  if (props.quiz === "Failed") return "Failed";
  if (props.quiz === "Passed and Done") return "Passed and Done";
  if (props.type === "MQ" && props.quiz === "Passed" && props.inspection === "None") {
    return "Passed and Done";
  }
  return "Passed";
});

const legacyQuizStatus = computed(() => {
  const s = props.checkpointInfo?.quiz_completion_status;
  if (["Passed", "Passed and Done", "Failed", "Pending"].includes(s)) return s;
  return "Passed";
});

const legacyInspectionStatus = computed(() => {
  const raw = (props.inspection_status || "").toLowerCase();
  if (raw === "passed") return "passed";
  if (raw === "failed") return "failed";
  if (raw === "n/a" || raw === "na" || raw === "not applicable") return "na";
  if (raw === "pending" || raw === "reset" || raw === "obsolete" || !raw) {
    return "waiting_for_coach";
  }
  return "waiting_for_coach";
});

function onMdpCtaClick() {
  const action = mdpCtaKind.value;
  if (action === "complete_microlearning") {
    emit("complete-microlearning-click");
  }
  emit("mdp-cta", { action });
}

const mdpChatterDrawer = ref(false);
const mdpChatterAvatar = ref({});
const mdpChatterTypeName = ref("Checkpoint");
const mdpChatterTypeValue = ref("");
const mdpChatterListData = ref({});
const chatterList = ref([]);
const totalChatter = ref(0);
const chatterPage = ref(1);
const chatterLoading = ref(false);

const mdpChatterList = chatterList;
const mdpChatterTotal = totalChatter;

const resolvedInspectionId = computed(() =>
  resolveCheckpointEvaluationRecordId({
    inspectionId: props.inspectionId,
    ...props.checkpointInfo,
  })
);

const resolvedAccountId = computed(() => resolveAccountIdFromStorage(props.accountId));

function syncChatterDrawerHeader() {
  const data = props.checkpointInfo || {};
  mdpChatterListData.value = data;
  mdpChatterTypeValue.value =
    data.criteria || props.criteria || effectiveCriteriaWithPrefix.value || "";
  const ui = data.userInfo || props.userInfo || {};
  mdpChatterAvatar.value = {
    picture: ui.inspected_by_avatar,
    firstName: ui.inspected_by,
  };
}

async function fetchChatter(page = 1, append = false) {
  const accountId = resolvedAccountId.value;
  const recordId = resolvedInspectionId.value;
  if (accountId == null || recordId == null || chatterLoading.value) return;

  chatterLoading.value = true;
  try {
    const { results, count } = await fetchCheckpointEvaluationChatter(accountId, recordId, page);
    totalChatter.value = count;
    chatterList.value = append ? [...chatterList.value, ...results] : results;
    chatterPage.value = page;
    emit("requestMoreCheckpointsChatter", props.checkpointInfo);
  } catch (error) {
    console.error("Error loading checkpoint evaluation chatter:", error);
  } finally {
    chatterLoading.value = false;
  }
}

async function openChatterDrawer() {
  chatterList.value = [];
  totalChatter.value = 0;
  chatterPage.value = 1;
  await fetchChatter(1, false);
}

async function loadMoreChatter() {
  if (totalChatter.value <= chatterList.value.length) return;
  await fetchChatter(chatterPage.value + 1, true);
}

async function openMdpChatterDrawer() {
  if (chatterLoading.value) return;

  syncChatterDrawerHeader();
  chatterList.value = [];
  totalChatter.value = 0;
  chatterPage.value = 1;
  await fetchChatter(1, false);
  mdpChatterDrawer.value = true;
}

function onMdpChatterIconActivate() {
  if (props.suppressMdpChatterDrawer) return;
  openMdpChatterDrawer();
}

function onMdpChatterDrawerClose(data) {
  if (data) {
    mdpChatterDrawer.value = false;
    chatterList.value = [];
    totalChatter.value = 0;
    chatterPage.value = 1;
  }
}
</script>

<style>
.ex-checkpoint-card {
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  padding: 0px 8px 8px 8px;
}

/*
 * Chatter hit target: match `chatter-type-list` / `chatter-type-icon` (e.g. AsEmployeeCheckpointCardActionsBar,
 * ExResponsibilityBadgeHolder) — same padding/margins on :hover so shadow renders cleanly without layout shift.
 */
.ex-mdp-chatter-icon-btn {
  flex-shrink: 0;
  align-self: center;
  display: grid;
  justify-content: end;
  box-sizing: border-box;
  padding: 6px;
  text-align: center;
  width: fit-content;
  cursor: url("../assets/cursorArrow.svg"), auto;
  border-radius: 8px;
}

.ex-mdp-chatter-icon-btn:hover {
  background-color: #f2edff;
  padding: 6px;
  text-align: center;
  box-shadow: 0px 4px 4px 0px #6f71f80f;
  border-radius: 8px;
}

.ex-mdp-chatter-icon-btn--inactive:hover {
  background-color: #f2edff;
  padding: 6px;
  text-align: center;
  box-shadow: 0px 4px 4px 0px #6f71f80f;
  border-radius: 8px;
}

.ex-mdp-chatter-icon-btn:focus-visible {
  outline: 2px solid #c3b5fd;
  outline-offset: 2px;
}

.ex-mdp-chatter-icon-btn--inactive:focus-visible {
  outline: none;
}

.ex-mdp-chatter-icon-btn__img {
  flex-shrink: 0;
}
.ex-checkpoint-area {
  margin-top: 0px;
}
.ex-checkpoint-card .ex-right-bar {
  margin-top: 5px;
}
.card-checkpoint-area {
  margin: 6px 0px 0px;
}

.ex-criteria-row {
  display: flex;
  align-items: stretch;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
  overflow: visible;
}

.ex-criteria-row .checkpoint-criteria-bar {
  flex: 1 1 auto;
  min-width: 0;
}

.ex-body {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  width: 100%;
  margin-top: 4px;
  box-sizing: border-box;
}

.ex-source {
  flex: 1 1 auto;
  min-width: 0;
}

.ex-badges {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 7px;
}

/* MDP-452 “Complete microlearning” CTA (custom); proceed / retake use StandardButton. */
.ex-mdp-cta-btn {
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  padding: 10px 12px 10px 14px;
  min-height: 89px;
  border: none;
  border-radius: 8px;
  background-color: #7f56d9;
  color: #ffffff;
  cursor: pointer;
  font-family:
    Inter,
    system-ui,
    -apple-system,
    "Segoe UI",
    Roboto,
    Arial,
    sans-serif;
  box-sizing: border-box;
  flex-shrink: 0;
  text-align: left;
  transition: background-color 0.15s ease;
}

.ex-mdp-cta-btn:hover {
  background-color: #6941c6;
}

.ex-mdp-cta-btn:active {
  background-color: #5925a8;
}

.ex-mdp-cta-btn:focus-visible {
  outline: 2px solid #c3b5fd;
  outline-offset: 2px;
}

.ex-mdp-standard-cta {
  flex-shrink: 0;
}

.ex-mdp-cta-btn__leading {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
}

.ex-mdp-cta-btn__doc-icon {
  display: block;
}

.ex-mdp-cta-btn__labels {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 6px;
  min-width: 0;
}

.ex-mdp-cta-btn__line {
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
  color: #ffffff;
}

.ex-mdp-cta-btn__chevron {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 2px;
  color: #ffffff;
}
</style>
